/**
 * User Detail Page
 * Comprehensive view of a single user: profile header, account balance,
 * flight history, licences, and aliases.
 *
 * Route: /users/:id  (admin-only)
 *
 * Requirements: 13.1–13.10
 */

import {
  getPilots,
  getPilotAccountBalance,
  getPilotAliases,
  getFlightsForUser,
  getPilotLicences,
  createPilotWithAuth,
  updatePilot,
} from '../services/api.js';
import { createDataGrid } from '../components/data-grid.js';
import { navigate } from '../services/router.js';
import { USER_ROLES } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for property-based testing)
// ---------------------------------------------------------------------------

/**
 * Determine whether an alias notice should be displayed for a pilot record.
 *
 * Exported for property-based testing (Property 23).
 *
 * @param {Object} pilot - A pilot record with at least `role`, `parent_id`, and optionally a parent object
 * @returns {{ show: boolean, parentName: string, parentId: string }}
 */
export function shouldShowAliasNotice(pilot) {
  if (!pilot || typeof pilot !== 'object') {
    return { show: false, parentName: '', parentId: '' };
  }
  if (pilot.role === 'alias') {
    return {
      show: true,
      parentName: (pilot.parent && pilot.parent.name) || pilot.parentName || '',
      parentId: pilot.parent_id || '',
    };
  }
  return { show: false, parentName: '', parentId: '' };
}

/**
 * Compute account balance from a list of account entries.
 *
 * Exported for property-based testing (Property 19).
 *
 * @param {Array<{ entry_type: string, amount: number|string }>} entries
 * @returns {{ credits: number, debits: number, balance: number }}
 */
export function computeAccountBalance(entries) {
  if (!Array.isArray(entries)) {
    return { credits: 0, debits: 0, balance: 0 };
  }
  const credits = entries
    .filter(e => e.entry_type === 'CREDIT')
    .reduce((sum, e) => sum + Number(e.amount), 0);
  const debits = entries
    .filter(e => e.entry_type === 'DEBIT')
    .reduce((sum, e) => sum + Number(e.amount), 0);
  return { credits, debits, balance: credits - debits };
}

/**
 * Filter flights for a parent user: include flights where pilot_id matches
 * the parent's UUID or any of the alias UUIDs.
 *
 * Exported for property-based testing (Property 20).
 *
 * @param {Object[]} flights - Array of flight records with `pilot_id`
 * @param {string} parentId - The parent user's UUID
 * @param {string[]} aliasIds - UUIDs of alias records
 * @returns {Object[]} Filtered flights
 */
export function filterFlightsForParent(flights, parentId, aliasIds) {
  if (!Array.isArray(flights)) return [];
  const validIds = new Set([parentId, ...(aliasIds || [])]);
  return flights.filter(f => validIds.has(f.pilot_id));
}

/**
 * Filter flights for an alias record: include only flights where pilot_id
 * matches the alias's own UUID.
 *
 * Exported for property-based testing (Property 21).
 *
 * @param {Object[]} flights - Array of flight records with `pilot_id`
 * @param {string} aliasId - The alias's UUID
 * @returns {Object[]} Filtered flights
 */
export function filterFlightsForAlias(flights, aliasId) {
  if (!Array.isArray(flights)) return [];
  return flights.filter(f => f.pilot_id === aliasId);
}

/**
 * Classify a licence's expiry status relative to a reference date.
 *
 * Exported for property-based testing (Property 22).
 *
 * @param {string|Date} expiryDate - The licence expiry date
 * @param {string|Date} today - The reference "today" date
 * @returns {'expired'|'expiring-soon'|'valid'}
 */
export function classifyLicenceExpiry(expiryDate, today) {
  const expiry = new Date(expiryDate);
  const todayDate = new Date(today);

  // Normalise to midnight UTC for date-only comparison
  expiry.setUTCHours(0, 0, 0, 0);
  todayDate.setUTCHours(0, 0, 0, 0);

  if (expiry < todayDate) return 'expired';

  const thirtyDaysLater = new Date(todayDate);
  thirtyDaysLater.setUTCDate(thirtyDaysLater.getUTCDate() + 30);

  if (expiry < thirtyDaysLater) return 'expiring-soon';

  return 'valid';
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatMinutes(mins) {
  if (mins == null) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function formatGBP(v) {
  if (v == null) return '';
  return `£${Number(v).toFixed(2)}`;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ---------------------------------------------------------------------------
// Section builders
// ---------------------------------------------------------------------------

/**
 * Build the profile header section.
 * Requirements: 13.3, 13.9
 */
function createProfileHeader(pilot) {
  const section = document.createElement('div');
  section.className = 'user-detail__profile';

  const heading = document.createElement('h2');
  heading.textContent = pilot.name || 'Unknown User';
  section.appendChild(heading);

  // Info grid
  const info = document.createElement('div');
  info.className = 'user-detail__info-grid';

  const fields = [
    { label: 'Email', value: pilot.email || '' },
    { label: 'Role', value: (pilot.role || '').charAt(0).toUpperCase() + (pilot.role || '').slice(1) },
    { label: 'Status', value: pilot.active === false ? 'Inactive' : 'Active' },
  ];

  for (const { label, value } of fields) {
    const row = document.createElement('div');
    row.className = 'user-detail__info-row';
    row.innerHTML = `<strong>${label}:</strong> <span>${escapeHtml(value)}</span>`;
    info.appendChild(row);
  }

  // Guest badge
  if (pilot.guest) {
    const badge = document.createElement('span');
    badge.className = 'badge badge--guest';
    badge.textContent = '🏷 Guest';
    info.appendChild(badge);
  }

  section.appendChild(info);

  // Alias notice (Requirement 13.9)
  const aliasInfo = shouldShowAliasNotice(pilot);
  if (aliasInfo.show) {
    const notice = document.createElement('div');
    notice.className = 'user-detail__alias-notice';
    notice.innerHTML = `⚠️ This user is an alias record. Parent user: <a href="#/users/${escapeHtml(aliasInfo.parentId)}" class="user-detail__parent-link">${escapeHtml(aliasInfo.parentName || 'Unknown')}</a>`;
    section.appendChild(notice);
  }

  return section;
}

/**
 * Build the account balance section.
 * Requirement: 13.4
 */
function createAccountBalanceSection(balance) {
  const section = document.createElement('div');
  section.className = 'user-detail__section';

  const title = document.createElement('h3');
  title.textContent = 'Account Balance';
  section.appendChild(title);

  const grid = document.createElement('div');
  grid.className = 'user-detail__balance-grid';
  grid.innerHTML = `
    <div class="user-detail__balance-item">
      <span class="user-detail__balance-label">Credits</span>
      <span class="user-detail__balance-value user-detail__balance-value--credit">${formatGBP(balance.credits)}</span>
    </div>
    <div class="user-detail__balance-item">
      <span class="user-detail__balance-label">Debits</span>
      <span class="user-detail__balance-value user-detail__balance-value--debit">${formatGBP(balance.debits)}</span>
    </div>
    <div class="user-detail__balance-item">
      <span class="user-detail__balance-label">Balance</span>
      <span class="user-detail__balance-value">${formatGBP(balance.balance)}</span>
    </div>
  `;
  section.appendChild(grid);

  return section;
}

/**
 * Build the flight history section.
 * Requirements: 13.5, 13.6
 */
function createFlightHistorySection(flights, pilot) {
  const section = document.createElement('div');
  section.className = 'user-detail__section';

  const title = document.createElement('h3');
  title.textContent = 'Flight History';
  section.appendChild(title);

  // If alias, show link back to parent
  if (pilot.role === 'alias' && pilot.parent_id) {
    const parentLink = document.createElement('p');
    parentLink.className = 'user-detail__alias-flight-note';
    parentLink.innerHTML = `Showing flights logged under this alias only. <a href="#/users/${escapeHtml(pilot.parent_id)}">View parent user's flights →</a>`;
    section.appendChild(parentLink);
  }

  const grid = createDataGrid({
    columns: [
      { key: 'flight_date', label: 'Date' },
      {
        key: 'aircraft_reg',
        label: 'Aircraft',
        format: (_v, row) => row.aircraft?.registration || '',
      },
      { key: 'departure', label: 'Departure' },
      { key: 'destination', label: 'Destination' },
      {
        key: 'flight_time_mins',
        label: 'Flight Time',
        align: 'right',
        format: (v) => formatMinutes(v),
      },
      {
        key: 'billing_status',
        label: 'Billing Status',
        format: (v) => v || '',
      },
      {
        key: 'logged_as',
        label: 'Logged As',
        format: (_v, row) => row.pilots?.name || '',
      },
    ],
    data: flights,
    defaultSort: 'flight_date',
    defaultOrder: 'desc',
    emptyMessage: 'No flights recorded',
    filterable: true,
  });

  section.appendChild(grid);
  return section;
}

/**
 * Build the licences section.
 * Requirement: 13.7
 */
function createLicencesSection(licences) {
  const section = document.createElement('div');
  section.className = 'user-detail__section';

  const title = document.createElement('h3');
  title.textContent = 'Licences';
  section.appendChild(title);

  if (!licences || licences.length === 0) {
    const empty = document.createElement('p');
    empty.textContent = 'No licences recorded';
    section.appendChild(empty);
    return section;
  }

  const today = new Date().toISOString().split('T')[0];

  const table = document.createElement('table');
  table.className = 'user-detail__licence-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th>Type</th>
        <th>Rating Category</th>
        <th>Expiry Date</th>
        <th>Status</th>
      </tr>
    </thead>
  `;

  const tbody = document.createElement('tbody');
  for (const lic of licences) {
    const tr = document.createElement('tr');
    const status = classifyLicenceExpiry(lic.expiry_date, today);

    let statusClass = '';
    let statusLabel = 'Valid';
    if (status === 'expired') {
      statusClass = 'licence--expired';
      statusLabel = 'Expired';
    } else if (status === 'expiring-soon') {
      statusClass = 'licence--expiring-soon';
      statusLabel = 'Expiring Soon';
    }

    tr.className = statusClass;
    tr.innerHTML = `
      <td>${escapeHtml(lic.licence_type || lic.type || '')}</td>
      <td>${escapeHtml(lic.rating_category || '')}</td>
      <td>${escapeHtml(lic.expiry_date || '')}</td>
      <td><span class="licence-status ${statusClass}">${statusLabel}</span></td>
    `;
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  section.appendChild(table);

  return section;
}

/**
 * Build the aliases section.
 * Requirements: 13.8, 13.10
 */
function createAliasesSection(aliases, pilotId, onAliasCreated) {
  const section = document.createElement('div');
  section.className = 'user-detail__section';

  const title = document.createElement('h3');
  title.textContent = 'Aliases';
  section.appendChild(title);

  if (aliases && aliases.length > 0) {
    const list = document.createElement('div');
    list.className = 'user-detail__alias-list';

    for (const alias of aliases) {
      const item = document.createElement('a');
      item.href = `#/users/${alias.id}`;
      item.className = 'user-detail__alias-item';
      item.innerHTML = `
        <span class="user-detail__alias-name">${escapeHtml(alias.name)}</span>
        <span class="user-detail__alias-desc">${escapeHtml(alias.description || '')}</span>
        <span class="user-detail__alias-flights">${alias.flight_count != null ? alias.flight_count + ' flights' : ''}</span>
      `;
      list.appendChild(item);
    }
    section.appendChild(list);
  } else {
    const empty = document.createElement('p');
    empty.textContent = 'No alias records';
    section.appendChild(empty);
  }

  // Create new alias form
  const formContainer = document.createElement('div');
  formContainer.className = 'user-detail__create-alias';

  const formTitle = document.createElement('h4');
  formTitle.textContent = 'Create New Alias';
  formContainer.appendChild(formTitle);

  const form = document.createElement('div');
  form.className = 'user-detail__alias-form';

  const nameInput = document.createElement('input');
  nameInput.type = 'text';
  nameInput.placeholder = 'Alias name (e.g. Training Flights)';
  nameInput.className = 'input';
  form.appendChild(nameInput);

  const descInput = document.createElement('input');
  descInput.type = 'text';
  descInput.placeholder = 'Description (optional)';
  descInput.className = 'input';
  form.appendChild(descInput);

  const createBtn = document.createElement('button');
  createBtn.className = 'btn btn--primary';
  createBtn.textContent = 'Create Alias';

  const formMessage = document.createElement('div');
  formMessage.className = 'user-detail__alias-form-message';

  createBtn.addEventListener('click', async () => {
    const name = nameInput.value.trim();
    if (!name) {
      formMessage.textContent = 'Please enter an alias name';
      formMessage.className = 'user-detail__alias-form-message user-detail__alias-form-message--error';
      return;
    }

    createBtn.disabled = true;
    createBtn.textContent = 'Creating…';
    formMessage.textContent = '';

    try {
      // Create a new pilot record with role 'alias' pointing to this user
      const { getSupabase } = await import('../services/supabase.js');
      const { error } = await getSupabase()
        .from('pilots')
        .insert({
          name,
          description: descInput.value.trim() || null,
          role: 'alias',
          parent_id: pilotId,
          active: false,
          email: `${name.toLowerCase().replace(/\s+/g, '-')}-alias-${Date.now()}@alias.local`,
        });
      if (error) throw error;

      nameInput.value = '';
      descInput.value = '';
      formMessage.textContent = 'Alias created successfully';
      formMessage.className = 'user-detail__alias-form-message user-detail__alias-form-message--success';

      if (onAliasCreated) onAliasCreated();
    } catch (err) {
      formMessage.textContent = err.message || 'Failed to create alias';
      formMessage.className = 'user-detail__alias-form-message user-detail__alias-form-message--error';
    } finally {
      createBtn.disabled = false;
      createBtn.textContent = 'Create Alias';
    }
  });

  form.appendChild(createBtn);
  formContainer.appendChild(form);
  formContainer.appendChild(formMessage);
  section.appendChild(formContainer);

  return section;
}

// ---------------------------------------------------------------------------
// Main render function
// ---------------------------------------------------------------------------

/**
 * Render the User Detail Page.
 * @param {{ params: { id: string } }} context - Route context with user ID
 */
export async function renderUserDetail({ params }) {
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="container">
      <div id="user-detail-container"><p>Loading user details…</p></div>
    </div>
  `;

  const container = document.getElementById('user-detail-container');
  if (!container) return;

  try {
    // Fetch the pilot record
    const allPilots = await getPilots();
    const pilot = allPilots.find(p => p.id === params.id);

    if (!pilot) {
      container.innerHTML = `
        <div class="user-not-found">
          <h2>User not found</h2>
          <p>The requested user could not be found.</p>
          <a href="#/settings" class="btn btn--primary">Back to User Manager</a>
        </div>
      `;
      return;
    }

    container.innerHTML = '';

    // Back link
    const backLink = document.createElement('a');
    backLink.href = '#/settings';
    backLink.className = 'user-detail__back';
    backLink.textContent = '← Back to User Manager';
    container.appendChild(backLink);

    // Profile header (Req 13.3, 13.9)
    container.appendChild(createProfileHeader(pilot));

    // Account balance (Req 13.4)
    let balance = { credits: 0, debits: 0, balance: 0 };
    try {
      balance = await getPilotAccountBalance(params.id);
    } catch (_) { /* non-critical */ }
    container.appendChild(createAccountBalanceSection(balance));

    // Flight history (Req 13.5, 13.6)
    let flights = [];
    try {
      if (pilot.role === 'alias') {
        // Alias: only flights for this alias's pilot_id
        flights = await getFlightsForUser(params.id, []);
      } else {
        // Parent user: include alias flights
        let aliases = [];
        try {
          aliases = await getPilotAliases(params.id);
        } catch (_) { /* non-critical */ }
        const aliasIds = aliases.map(a => a.id);
        flights = await getFlightsForUser(params.id, aliasIds);
      }
    } catch (_) { /* non-critical */ }
    container.appendChild(createFlightHistorySection(flights, pilot));

    // Licences (Req 13.7)
    let licences = [];
    try {
      licences = await getPilotLicences(params.id);
    } catch (_) { /* non-critical */ }
    container.appendChild(createLicencesSection(licences));

    // Aliases section (Req 13.8) — only for non-alias users
    if (pilot.role !== 'alias') {
      let aliases = [];
      try {
        aliases = await getPilotAliases(params.id);
      } catch (_) { /* non-critical */ }

      const aliasSection = createAliasesSection(aliases, params.id, () => {
        // Refresh the page on alias creation
        renderUserDetail({ params });
      });
      container.appendChild(aliasSection);
    }

  } catch (err) {
    container.innerHTML = `
      <div class="user-not-found">
        <h2>Error loading user</h2>
        <p>Failed to load user details. Please try again.</p>
        <a href="#/settings" class="btn btn--primary">Back to User Manager</a>
      </div>
    `;
    console.error('Failed to load user detail:', err);
  }
}
